import { FormEvent, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { type Persona } from '@/stores/personasStore';
import { type AddTargetInput, type AlertFrequency, type TargetType } from '@/stores/targetsStore';

const LINKEDIN_URL_REGEX = /^https?:\/\/(www\.)?linkedin\.com\/in\/.+/i;

interface AddTargetModalProps {
  isOpen: boolean;
  isLoading?: boolean;
  personas?: Persona[];
  defaultPersonaId?: string;
  onClose: () => void;
  onSave: (data: AddTargetInput) => Promise<void>;
}

interface FormState {
  linkedin_url: string;
  nickname: string;
  notes: string;
  persona_id: string;
  type: TargetType;
  priority: string;
  notify_on_post: boolean;
  notify_on_comment: boolean;
  alert_frequency: AlertFrequency;
  relationship_tags: string;
  is_active: boolean;
}

function getInitialState(defaultPersonaId = ''): FormState {
  return {
    linkedin_url: '',
    nickname: '',
    notes: '',
    persona_id: defaultPersonaId,
    type: 'followed',
    priority: 'medium',
    notify_on_post: true,
    notify_on_comment: false,
    alert_frequency: 'daily',
    relationship_tags: '',
    is_active: true,
  };
}

const fieldShell = 'space-y-2';
const labelClass = 'brutalist-label';
const selectClass = 'flex h-10 w-full rounded-xl border-2 border-border bg-background px-3 py-1 text-sm font-bold shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';
const inputClass = 'h-10 rounded-xl border-2 border-border bg-background font-bold shadow-sm';

export function AddTargetModal({
  isOpen,
  isLoading = false,
  personas = [],
  defaultPersonaId,
  onClose,
  onSave,
}: AddTargetModalProps) {
  const [form, setForm] = useState<FormState>(getInitialState(defaultPersonaId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setForm(getInitialState(defaultPersonaId ?? personas[0]?.id ?? ''));
      setError(null);
    }
  }, [defaultPersonaId, isOpen, personas]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const linkedinUrl = form.linkedin_url.trim();

    if (!linkedinUrl) {
      setError('LinkedIn URL obbligatoria.');
      return;
    }

    if (!LINKEDIN_URL_REGEX.test(linkedinUrl)) {
      setError('Inserisci una URL LinkedIn valida (es. https://linkedin.com/in/nome).');
      return;
    }

    try {
      await onSave({
        linkedin_url: linkedinUrl,
        nickname: form.nickname.trim() || undefined,
        notes: form.notes.trim() || undefined,
        persona_id: form.persona_id || undefined,
        type: form.type,
        priority: form.priority as 'high' | 'medium' | 'low',
        notify_on_post: form.notify_on_post,
        notify_on_comment: form.notify_on_comment,
        alert_frequency: form.alert_frequency,
        relationship_tags: form.relationship_tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        is_active: form.is_active,
      });
      onClose();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Errore durante il salvataggio della persona di rete.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/70 p-4 backdrop-blur-sm">
      <div
        className="brutalist-panel relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-target-modal-title"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-xl border-2 border-border bg-background text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          aria-label="Chiudi modal aggiunta persona network"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="border-b-2 border-border p-6 pr-16">
          <p className="brutalist-label">Network Intelligence</p>
          <h2 id="add-target-modal-title" className="mt-2 text-3xl font-black tracking-[-0.08em]">Aggiungi persona alla Network Intelligence</h2>
          <p className="mt-2 text-sm font-medium text-muted-foreground">
            Collega un profilo LinkedIn a una persona gestita e definisci categoria, priorita e alert.
          </p>
        </div>

        <div className="p-6">
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className={fieldShell}>
              <label className={labelClass} htmlFor="target-linkedin-url">LinkedIn URL *</label>
              <Input
                id="target-linkedin-url"
                className={inputClass}
                value={form.linkedin_url}
                onChange={(event) => setForm((prev) => ({ ...prev, linkedin_url: event.target.value }))}
                placeholder="https://linkedin.com/in/username"
                required
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className={fieldShell}>
                <label className={labelClass} htmlFor="target-persona">Persona gestita</label>
                <select
                  id="target-persona"
                  className={selectClass}
                  value={form.persona_id}
                  onChange={(event) => setForm((prev) => ({ ...prev, persona_id: event.target.value }))}
                >
                  <option value="">Unassigned / shared</option>
                  {personas.map((persona) => (
                    <option key={persona.id} value={persona.id}>{persona.name}</option>
                  ))}
                </select>
              </div>
              <div className={fieldShell}>
                <label className={labelClass} htmlFor="target-type">Categoria</label>
                <select
                  id="target-type"
                  className={selectClass}
                  value={form.type}
                  onChange={(event) => setForm((prev) => ({ ...prev, type: event.target.value as TargetType }))}
                >
                  <option value="followed">Followed people</option>
                  <option value="inspirator">Inspirators</option>
                  <option value="impress">Impress targets</option>
                </select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className={fieldShell}>
                <label className={labelClass} htmlFor="target-nickname">Nickname</label>
                <Input id="target-nickname" className={inputClass} value={form.nickname} onChange={(event) => setForm((prev) => ({ ...prev, nickname: event.target.value }))} placeholder="Nome interno" />
              </div>
              <div className={fieldShell}>
                <label className={labelClass} htmlFor="target-priority">Priority</label>
                <select
                  id="target-priority"
                  className={selectClass}
                  value={form.priority}
                  onChange={(event) => setForm((prev) => ({ ...prev, priority: event.target.value }))}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>

            <div className={fieldShell}>
              <label className={labelClass} htmlFor="target-tags">Relationship tags</label>
              <Input id="target-tags" className={inputClass} value={form.relationship_tags} onChange={(event) => setForm((prev) => ({ ...prev, relationship_tags: event.target.value }))} placeholder="investor, prospect, creator" />
            </div>

            <div className={fieldShell}>
              <label className={labelClass} htmlFor="target-notes">Notes</label>
              <textarea
                id="target-notes"
                className="min-h-24 w-full rounded-xl border-2 border-border bg-background px-3 py-2 text-sm font-bold shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={form.notes}
                onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
                placeholder="Perché questa persona conta per la persona gestita"
              />
            </div>

            <div className="brutalist-panel-soft rounded-2xl bg-background/45 p-4">
              <p className="brutalist-label mb-3">Alerts</p>
              <div className="grid gap-3 md:grid-cols-3">
                <label className="flex items-center gap-2 rounded-xl border-2 border-border bg-card px-3 py-2 text-sm font-black"><input type="checkbox" checked={form.notify_on_post} onChange={(event) => setForm((prev) => ({ ...prev, notify_on_post: event.target.checked }))} /> Posts</label>
                <label className="flex items-center gap-2 rounded-xl border-2 border-border bg-card px-3 py-2 text-sm font-black"><input type="checkbox" checked={form.notify_on_comment} onChange={(event) => setForm((prev) => ({ ...prev, notify_on_comment: event.target.checked }))} /> Comments</label>
                <label className="flex items-center gap-2 rounded-xl border-2 border-border bg-card px-3 py-2 text-sm font-black"><input type="checkbox" checked={form.is_active} onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))} /> Active</label>
              </div>
              <label className="brutalist-label mt-3 block" htmlFor="target-alert-frequency">Alert frequency</label>
              <select
                id="target-alert-frequency"
                className={`${selectClass} mt-2`}
                value={form.alert_frequency}
                onChange={(event) => setForm((prev) => ({ ...prev, alert_frequency: event.target.value as AlertFrequency }))}
              >
                <option value="instant">Instant</option>
                <option value="daily">Daily digest</option>
                <option value="weekly">Weekly digest</option>
                <option value="off">Off</option>
              </select>
            </div>

            {error ? <p className="rounded-2xl border-2 border-destructive/70 bg-destructive/5 p-3 text-sm font-bold text-destructive">{error}</p> : null}

            <div className="flex flex-wrap justify-end gap-2 border-t-2 border-border pt-5">
              <Button type="button" variant="outline" className="rounded-xl border-2 border-border font-black" onClick={onClose} disabled={isLoading}>Annulla</Button>
              <Button type="submit" className="rounded-xl border-2 border-border bg-primary font-black text-primary-foreground shadow-[4px_4px_0_hsl(var(--foreground)/0.18)]" disabled={isLoading}>{isLoading ? 'Salvataggio...' : 'Aggiungi alla network'}</Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
